import { useQueryClient } from '@tanstack/react-query'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import PropTypes from 'prop-types'
import { useEffect, useContext, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { Link } from 'react-router'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import {
  Form,
  FormControl,
  FormLabel,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/elements/input-otp'
import { usePhoneMask, cleanPhone, formatPhone } from '@/hooks/PhoneNumberMask'
import { requestPhoneChange, confirmPhoneChange } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import { SharedCardLayout } from './layout'

/**
 * Handles requesting a change to the user's phone number, and sending the verification code.
 * Shared by both PhoneNumberSection and VerifyPhoneNumberDialog.
 *
 * @param {object} data
 * @param {string} data.phoneNumber
 * @param {function} data.onSuccess
 * @param {function} data.onError
 * @param {boolean} data.setLoading
 * @returns {Promise<void>}
 */
const handleRequestPhoneChange = async ({ phoneNumber, onSuccess, onError, setLoading }) => {
  await requestPhoneChange(phoneNumber, onError, setLoading, (message) => {
    if (phoneNumber && phoneNumber.length > 0) {
      onSuccess?.(message || 'Verification code sent.')
    } else {
      onSuccess?.('Phone number removed.')
    }
  })
}

const VerifyPhoneNumberDialog = ({
  open,
  setOpen,
  phoneNumber,
  onCancel,
  handleSuccess,
  handleError,
}) => {
  const CODE_LENGTH = 6
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)

  const form = useForm({
    defaultValues: {
      code: '',
    },
  })

  const onSubmit = async (data) => {
    await confirmPhoneChange(data.code, handleError, setLoading, (msg) => {
      setLoading(true)
      handleSuccess(msg)
      setOpen(false)

      // finally, refresh the user and reset the form
      queryClient.invalidateQueries({ queryKey: ['users'] })
      form.reset({ code: '' })
      setLoading(false)
    })
  }

  /**
   * Handles resending the verification code with debounce of 10 seconds.
   *
   * @returns {Promise<void>}
   */
  const handleResendCode = () => {
    if (timeoutRef.current || loading) return

    // cooldown for 10 seconds
    setCooldown(10)

    // Start interval for countdown display
    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)

    // Lock resend logic
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
    }, 10_000)

    handleRequestPhoneChange({
      phoneNumber,
      onSuccess: handleSuccess,
      onError: handleError,
      setLoading,
    })
  }

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleCancel = () => {
    setOpen(false)
    onCancel()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        onInteractOutside={(e) => {
          e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>Verify Your Phone Number</DialogTitle>
          <DialogDescription>
            We&apos;ve sent a verification code to {formatPhone(phoneNumber)}.
            <br />
            Didn&apos;t receive it?{' '}
            {cooldown > 0 ? (
              <span className="text-primary text-sm font-medium">
                Try again in {cooldown} seconds.
              </span>
            ) : (
              <Button
                asChild
                disabled={loading}
                type="button"
                variant="link"
                size="plain"
                onClick={handleResendCode}
              >
                <span className="text-primary text-sm font-medium">Resend Code</span>
              </Button>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 flex w-full flex-col">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                name="code"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="justify-center">
                    <FormControl>
                      <InputOTP maxLength={CODE_LENGTH} pattern={REGEXP_ONLY_DIGITS} {...field}>
                        <InputOTPGroup>
                          {[...Array(CODE_LENGTH)].map((_, index) => (
                            <InputOTPSlot
                              // eslint-disable-next-line react/no-array-index-key
                              key={`slot-${index}`}
                              index={index}
                              autoFocus={index === 0}
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                disabled={loading}
                rules={{ required: true }}
              />

              <div className="mt-8 flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading}>
                  Verify Phone
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

VerifyPhoneNumberDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  phoneNumber: PropTypes.string,
  handleSuccess: PropTypes.func,
  handleError: PropTypes.func,
}

const PhoneNumberSection = () => {
  const { user } = useContext(UserStoreContext)
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleSuccess = (message) => toast(message, 'success')
  const handleError = (message) => toast(message, 'error')

  const form = useForm({
    defaultValues: {
      phoneNumber: '',
    },
  })

  useEffect(() => {
    if (user) {
      form.reset({
        phoneNumber: user.phoneNumber,
      })
    }
  }, [form, user, user.phoneNumber])

  /**
   * Requests changing the user's phone number, and sends the verification code.
   *
   * @param {object} data
   */
  const onSubmit = async (data) => {
    await handleRequestPhoneChange({
      phoneNumber: data.phoneNumber,
      onSuccess: (msg) => {
        handleSuccess(msg)

        // Unless the user is removing their phone number, show the dialog
        // NOTE: check data.phoneNumber is not an empty string, because the
        // input mask will always default it to an empty string
        if (data.phoneNumber !== '') {
          setDialogOpen(true)
        }
      },
      onError: (message) => {
        handleError(message)
        form.setError('phoneNumber', {
          type: 'manual',
          message: message || 'Something went wrong',
        })
      },
    })
  }

  return (
    <>
      <SharedCardLayout title="Phone Number">
        <SharedCardLayout.Form formProps={form}>
          <SharedCardLayout.Fields className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-1 sm:w-10/12">
              {user.phoneNumber
                ? 'Update the phone number associated with your account.'
                : 'Add a phone number to your account.'}{' '}
              By providing a phone number, you agree to receive SMS messages. Message and data
              rates may apply.
              <span className="mt-4 block text-sm text-gray-500">
                For more information, review our{' '}
                <Link className="text-primary underline" to="/privacy-policy" target="_blank">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link className="text-primary underline" to="/terms-of-use" target="_blank">
                  Terms of Use
                </Link>
                .
              </span>
            </div>

            <div className="col-span-1 space-y-6">
              <FormField
                control={form.control}
                name="phoneNumber"
                rules={{
                  validate: {
                    valid: (value) =>
                      !value || isPossiblePhoneNumber(value, 'US') || 'Enter a valid phone number',
                  },
                }}
                render={({ field }) => {
                  // FIXME: This hook is called conditionally
                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  const { masked } = usePhoneMask(field.value ?? '')

                  return (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={masked}
                          onChange={(e) => {
                            const raw = cleanPhone(e.target.value)
                            field.onChange(raw)
                          }}
                          type="tel"
                          maxLength={14}
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="(555) 555-1234"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            </div>
          </SharedCardLayout.Fields>

          <SharedCardLayout.Footer>
            <Button
              type="button"
              loading={dialogOpen}
              disabled={dialogOpen || !form.formState.isDirty}
              onClick={form.handleSubmit(onSubmit)}
            >
              Save Changes
            </Button>
          </SharedCardLayout.Footer>
        </SharedCardLayout.Form>
      </SharedCardLayout>

      <VerifyPhoneNumberDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        phoneNumber={form.getValues().phoneNumber}
        handleSuccess={handleSuccess}
        handleError={handleError}
        onCancel={() => {
          form.reset({ phoneNumber: user?.phoneNumber || '' })
          handleError('Phone number verification cancelled.')
        }}
      />
    </>
  )
}

export default PhoneNumberSection
